import { FormEvent, useCallback, useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import ConfirmDialog from '../ConfirmDialog'
import Spinner from '../Spinner'
import { newsletterAPI } from '../../services/api'
import type {
  AddSubscribersResult,
  NewsletterCampaign,
  NewsletterStatus,
  NewsletterSubscribers,
} from '../../types'
import { apiErrorMessage } from '../../utils/apiError'

const CONSENT_LABELS: Record<string, string> = {
  signup_form: 'Signed up and confirmed',
  added_by_creator: 'Added by you',
  convertkit: 'From ConvertKit',
}

const pct = (rate: number) => `${Math.round(rate * 100)}%`
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '')

/** The body is written as plain text (Markdown works); the email is its HTML. */
const toHtml = (text: string) => DOMPurify.sanitize(marked.parse(text, { async: false }))

/** Who gets the newsletter and how each agreed; adding people who agreed. */
const Subscribers = ({ onChange }: { onChange: () => void }) => {
  const [list, setList] = useState<NewsletterSubscribers | null>(null)
  const [addresses, setAddresses] = useState('')
  const [consent, setConsent] = useState(false)
  const [adding, setAdding] = useState(false)
  const [result, setResult] = useState<AddSubscribersResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    newsletterAPI
      .subscribers()
      .then(setList)
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load your list.")))
  }, [])
  useEffect(load, [load])

  const add = async (e: FormEvent) => {
    e.preventDefault()
    setAdding(true)
    setError(null)
    try {
      setResult(await newsletterAPI.addSubscribers(addresses, consent))
      setAddresses('')
      setConsent(false)
      load()
      onChange()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't add them."))
    } finally {
      setAdding(false)
    }
  }

  const remove = async (id: number) => {
    try {
      await newsletterAPI.removeSubscriber(id)
      load()
      onChange()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't remove them."))
    }
  }

  return (
    <section className="rounded-lg bg-white p-4 shadow" aria-label="Subscribers">
      <h2 className="text-lg font-semibold text-gray-900">
        Subscribers{list ? ` (${list.subscribed})` : ''}
      </h2>
      <form onSubmit={add} className="mt-3 space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="newsletter-add">
          Add people who asked for your newsletter
        </label>
        <textarea
          id="newsletter-add"
          value={addresses}
          onChange={(e) => setAddresses(e.target.value)}
          rows={3}
          placeholder="Email addresses, one per line or separated by commas"
          className="w-full rounded-md border border-gray-300 p-2 text-sm"
        />
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>Each of these people agreed to get my newsletter.</span>
        </label>
        <button
          type="submit"
          disabled={adding || !consent || addresses.trim().length < 3}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {adding ? 'Adding…' : 'Add'}
        </button>
      </form>
      {result && (
        <p role="status" className="mt-2 text-sm text-gray-700" data-testid="add-result">
          {[
            `${result.added.length} added`,
            result.already_subscribed.length > 0 && `${result.already_subscribed.length} already on the list`,
            result.unsubscribed.length > 0 &&
              `${result.unsubscribed.length} unsubscribed before, so not added (only they can sign up again): ${result.unsubscribed.join(', ')}`,
            result.invalid.length > 0 && `not email addresses: ${result.invalid.join(', ')}`,
          ]
            .filter(Boolean)
            .join('. ')}
          .
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
      {list && list.members.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 text-sm" aria-label="Subscriber list">
          {list.members.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-3 py-1.5">
              <span className={m.subscribed ? 'text-gray-900' : 'text-gray-400 line-through'}>{m.email}</span>
              <span className="text-xs text-gray-500">
                {m.unsubscribed_at
                  ? `Unsubscribed ${day(m.unsubscribed_at)}`
                  : `${CONSENT_LABELS[m.consent_source ?? ''] ?? 'Agreed'}${m.consent_at ? ` ${day(m.consent_at)}` : ''}`}
              </span>
              {m.subscribed && (
                <button
                  type="button"
                  onClick={() => remove(m.id)}
                  className="ml-auto text-xs text-gray-500 hover:underline"
                  aria-label={`Remove ${m.email}`}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

interface EditorProps {
  campaignId: number | null
  subscribed: number
  canSend: boolean
  onDone: () => void
}

/** Writing an issue: save, preview as sent, test to yourself, send. */
const Editor = ({ campaignId, subscribed, canSend, onDone }: EditorProps) => {
  const [id, setId] = useState<number | null>(campaignId)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(campaignId !== null)
  const [busy, setBusy] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    if (campaignId === null) return
    newsletterAPI
      .campaign(campaignId)
      .then((c) => {
        setSubject(c.subject)
        setBody(c.text_body ?? c.html_body)
      })
      .catch((e) => setError(apiErrorMessage(e, "Couldn't open this issue.")))
      .finally(() => setLoading(false))
  }, [campaignId])

  const save = async (): Promise<number> => {
    const data = { subject: subject.trim(), html_body: toHtml(body), text_body: body }
    if (id === null) {
      const created = await newsletterAPI.create(data)
      setId(created.id)
      return created.id
    }
    await newsletterAPI.update(id, data)
    return id
  }

  const run = async (what: string, job: (savedId: number) => Promise<void>) => {
    setBusy(what)
    setError(null)
    setMessage(null)
    try {
      await job(await save())
    } catch (e) {
      setError(apiErrorMessage(e, 'Something went wrong.'))
    } finally {
      setBusy(null)
    }
  }

  if (loading) return <Spinner size="sm" />
  const ready = subject.trim().length > 0 && body.trim().length > 0

  return (
    <section className="rounded-lg bg-white p-4 shadow" aria-label="Write an issue">
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Subject
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={255}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Body
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="mt-1 w-full rounded-md border border-gray-300 p-2 font-mono text-sm"
          />
        </label>
        <p className="text-xs text-gray-500">
          Plain text; Markdown works for links, **bold** and lists. Every issue gets &ldquo;Not financial advice&rdquo; if
          it's missing, an unsubscribe link and the postal address. Results need &ldquo;before fees&rdquo; and their dates,
          like every post.
        </p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!ready || !!busy}
          onClick={() => run('save', async () => setMessage('Saved.'))}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'save' ? 'Saving…' : 'Save draft'}
        </button>
        <button
          type="button"
          disabled={!ready || !!busy}
          onClick={() =>
            run('preview', async (savedId) => {
              setPreview(null)
              setPreview(DOMPurify.sanitize((await newsletterAPI.preview(savedId)).html))
            })
          }
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'preview' ? 'Checking…' : 'Preview as sent'}
        </button>
        <button
          type="button"
          disabled={!ready || !!busy || !canSend}
          onClick={() =>
            run('test', async (savedId) => {
              await newsletterAPI.sendTest(savedId)
              setMessage('Test sent to your email address.')
            })
          }
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'test' ? 'Sending…' : 'Send me a test'}
        </button>
        <button
          type="button"
          disabled={!ready || !!busy || !canSend || subscribed === 0}
          onClick={() => setConfirming(true)}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Send to {subscribed} {subscribed === 1 ? 'subscriber' : 'subscribers'}
        </button>
        <button type="button" onClick={onDone} className="ml-auto px-2 py-1.5 text-sm text-gray-600">
          Close
        </button>
      </div>
      {message && (
        <p role="status" className="mt-2 text-sm text-gray-700">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
      {preview && (
        <div
          className="prose prose-sm mt-4 max-w-none rounded-md border border-gray-200 p-4"
          data-testid="newsletter-preview"
          dangerouslySetInnerHTML={{ __html: preview }}
        />
      )}
      <ConfirmDialog
        isOpen={confirming}
        title="Send this issue?"
        message={`It goes to ${subscribed} ${subscribed === 1 ? 'person' : 'people'} now and can't be unsent.`}
        confirmLabel="Send"
        confirming={busy === 'send'}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          run('send', async (savedId) => {
            const sent = await newsletterAPI.send(savedId)
            setConfirming(false)
            if (sent.status === 'sent') onDone()
            else setError(`Nothing was sent: ${sent.failed_count} failed. Try again later.`)
          }).finally(() => setConfirming(false))
        }
      />
    </section>
  )
}

/**
 * Content → Newsletter: the list (who agreed, and how), issues written and
 * sent from FinCreator, and how each did. Sending needs email set up on the
 * server and a postal address for the footer; until then it says so.
 */
const NewsletterTab = () => {
  const [status, setStatus] = useState<NewsletterStatus | null>(null)
  const [campaigns, setCampaigns] = useState<NewsletterCampaign[] | null>(null)
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    Promise.all([newsletterAPI.status(), newsletterAPI.campaigns()])
      .then(([s, c]) => {
        setStatus(s)
        setCampaigns(c)
      })
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load your newsletter.")))
  }, [])
  useEffect(load, [load])

  const remove = async (id: number) => {
    try {
      await newsletterAPI.remove(id)
      load()
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't delete it."))
    }
  }

  if (error && !status) {
    return (
      <p role="alert" className="text-sm text-danger-700">
        {error}
      </p>
    )
  }
  if (!status || !campaigns) return <Spinner size="sm" />

  return (
    <div className="space-y-6">
      {!status.can_send && (
        <p role="note" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {status.reason} You can still build your list and write issues.
        </p>
      )}
      {status.accepting_signups ? (
        <p className="text-sm text-gray-600">
          People can sign up on your page, <a href={status.signup_path} className="text-primary-600 hover:underline">{status.signup_path}</a>.
          They confirm by email before they're added.
        </p>
      ) : (
        status.can_send && (
          <p className="text-sm text-gray-600">
            A sign-up form appears on your page ({status.signup_path}) once you've written your first issue.
          </p>
        )
      )}

      {editing !== null ? (
        <Editor
          campaignId={editing === 'new' ? null : editing}
          subscribed={status.subscribed}
          canSend={status.can_send}
          onDone={() => {
            setEditing(null)
            load()
          }}
        />
      ) : (
        <section className="rounded-lg bg-white p-4 shadow" aria-label="Issues">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Issues</h2>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
            >
              Write an issue
            </button>
          </div>
          {campaigns.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No issues yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-gray-100" aria-label="Issue list">
              {campaigns.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm" data-testid="issue">
                  <span className="font-medium text-gray-900">{c.subject}</span>
                  {c.status === 'sent' ? (
                    <span className="text-gray-600">
                      Sent {day(c.sent_at)} to {c.sent_count} · {pct(c.open_rate)} opened · {pct(c.click_rate)} clicked
                    </span>
                  ) : (
                    <span className="text-gray-500">{c.status === 'failed' ? 'Not sent (failed)' : 'Draft'}</span>
                  )}
                  {c.status !== 'sent' && (
                    <span className="ml-auto flex gap-3 text-xs">
                      <button type="button" onClick={() => setEditing(c.id)} className="text-primary-600 hover:underline">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(c.id)}
                        className="text-gray-500 hover:underline"
                        aria-label={`Delete ${c.subject}`}
                      >
                        Delete
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Opens are counted when an email app loads images, so they undercount; clicks are counted on links in the
            issue.
          </p>
        </section>
      )}

      <Subscribers onChange={load} />
    </div>
  )
}

export default NewsletterTab
